// src/components/heatmap/HeatmapWidget.tsx
import { useMemo, useEffect, useState } from "react";
import { MapContainer, TileLayer, Pane, useMap } from "react-leaflet";
import "leaflet/dist/leaflet.css";
import { LeafletHeatLayer } from "@/lib/heatmap";
import CurrentLocationDot from "@/components/heatmap/CurrentLocationDot";
import MapAutoResize from "@/components/heatmap/MapAutoResize";
import LgsMarkers from "@/components/heatmap/LgsMarkers";
import { useHeatmapInitWidget } from "@/lib/useHeatmapInit";
import {
  useFilteredHeatPoints,
  useLgsPoints,
  useHeatLoading,
  useHeatError,
  useLgsError,
  useHeatmapStore,
} from "@/lib/heatmapStore";
import { HeatmapLegend } from "@/components/heatmap/HeatmapLegend";
import { useNavigate } from "react-router-dom";
import { publishUserHeatPing } from "@/lib/publishHeatPing";

type ObjPoint = { lat: number; lng: number; intensity?: number };

const CITY_ZOOM = 11; // city-level; raise to 12/13 for tighter view

// Replace FitLogic component with unified AutoCenter component:
function AutoCenterOnLoad({ points }: { points: ObjPoint[] }) {
  const map = useMap();
  useEffect(() => {
    let canceled = false;
    let centered = false;

    const fitHeatBounds = () => {
      if (canceled || centered || !points.length) return;
      try {
        const L = (window as any).L;
        if (!L) return;
        const b = L.latLngBounds(points.map((p) => [p.lat, p.lng]));
        if ((map as any)?._loaded) {
          map.fitBounds(b.pad(0.3));
          centered = true;
        }
      } catch {}
    };

    const geoSuccess = (p: GeolocationPosition, mode: "login" | "fallback") => {
      if (canceled || centered) return;
      const { latitude, longitude } = p.coords;
      publishUserHeatPing(latitude, longitude, mode).catch(() => {});
      const attempt = () => {
        if (canceled || centered) return;
        if ((map as any)?._loaded) {
          try {
            map.setView([latitude, longitude], Math.max(map.getZoom(), 11), {
              animate: true,
            });
            centered = true;
            // kick nearby LGS refresh with user location
            useHeatmapStore
              .getState()
              .fetchNearbyLgs({ lat: latitude, lng: longitude, force: true });
          } catch {}
        } else {
          requestAnimationFrame(attempt);
        }
      };
      requestAnimationFrame(attempt);
    };

    if ("geolocation" in navigator) {
      navigator.geolocation.getCurrentPosition(
        (pos) => geoSuccess(pos, "login"),
        () => {
          // fallback to bounds after delay
          setTimeout(fitHeatBounds, 200);
        },
        { enableHighAccuracy: true, timeout: 6000 }
      );
      // secondary fallback if geo hangs
      setTimeout(() => {
        if (!centered) fitHeatBounds();
      }, 1500);
    } else {
      fitHeatBounds();
    }

    return () => {
      canceled = true;
    };
  }, [map, points]);

  return null;
}

function HeatmapWidgetImpl() {
  const navigate = useNavigate();
  useHeatmapInitWidget();

  const heatLoading = useHeatLoading();
  const heatError = useHeatError();
  const lgsError = useLgsError();
  const heatCells = useHeatmapStore((s) => s.heatPoints.length);
  const lgsPoints = useLgsPoints();
  const filteredTriplets = useFilteredHeatPoints();
  const [showNotice, setShowNotice] = useState(false);

  const points: ObjPoint[] = useMemo(
    () =>
      filteredTriplets?.length
        ? filteredTriplets.map(([lat, lng, w]) => ({
            lat,
            lng,
            intensity: w,
          }))
        : [],
    [filteredTriplets]
  );

  const faded = heatLoading || !!heatError || !!lgsError;

  useEffect(() => {
    if (import.meta.env.DEV) {
      console.log("[widget] heat points", points.length);
    }
  }, [points]);

  function ExposeMap() {
    const map = useMap();
    useEffect(() => {
      (window as any)._heatWidgetMap = map;
    }, [map]);
    return null;
  }

  return (
    <div className="dashboard-heatmap-wrapper space-y-2 relative">
      <div
        className="relative rounded overflow-hidden border border-neutral-800/60 bg-black"
        style={{ height: 240 }}
      >
        <div
          className="absolute inset-0 transition-opacity duration-500"
          style={{ opacity: faded ? 0.35 : 1, zIndex: 0 }}
        >
          <MapContainer
            center={[20, 0]}
            zoom={2}
            minZoom={2}
            attributionControl={false}
            scrollWheelZoom
            className="w-full h-full"
          >
            <TileLayer
              url="https://{s}.basemaps.cartocdn.com/dark_all/{z}/{x}/{y}{r}.png"
              attribution=""
            />
            {points.length > 0 && (
              <Pane
                name="heatmap-pane-mini"
                style={{ zIndex: 350, pointerEvents: "none" }}
              >
                <LeafletHeatLayer
                  points={points.map((p) => [p.lat, p.lng, p.intensity ?? 0.6])}
                  radius={25}
                  blur={18}
                  maxZoom={12}
                  interactive={false}
                  pane="heatmap-pane-mini"
                />
              </Pane>
            )}
            <LgsMarkers />
            <CurrentLocationDot />
            <MapAutoResize />
            <AutoCenterOnLoad points={points} />
            <ExposeMap />
          </MapContainer>
        </div>

        {faded && (
          <div className="pointer-events-none absolute inset-0 bg-black/30 backdrop-blur-[1px] transition-opacity duration-500" />
        )}

        <div className="absolute inset-0 z-[900] pointer-events-none">
          <div className="absolute top-2 left-2 flex gap-2 pointer-events-auto">
            {/* Center on me */}
            <button
              onClick={() => {
                if ("geolocation" in navigator) {
                  navigator.geolocation.getCurrentPosition(
                    (p) => {
                      const leafletMap = (window as any)._heatWidgetMap;
                      if (!leafletMap) return;
                      const doSet = () => {
                        if (leafletMap._loaded) {
                          try {
                            leafletMap.setView(
                              [p.coords.latitude, p.coords.longitude],
                              Math.max(leafletMap.getZoom(), 6),
                              { animate: true }
                            );
                          } catch {}
                        } else {
                          requestAnimationFrame(doSet);
                        }
                      };
                      requestAnimationFrame(doSet);
                    },
                    () => {}
                  );
                }
              }}
              className="px-2 py-1 text-[11px] rounded bg-neutral-900/80 border border-neutral-700 text-neutral-200 hover:bg-neutral-800"
              title="Center on me"
            >
              Me
            </button>
            <button
              onClick={() => useHeatmapStore.getState().refreshLgs()}
              className="px-2 py-[2px] text-[10px] rounded bg-neutral-800 border border-neutral-600 hover:bg-neutral-700"
            >
              Refresh LGS
            </button>
            <button
              onClick={() => setShowNotice((v) => !v)}
              className="px-2 py-1 text-[11px] rounded bg-neutral-900/80 border border-neutral-700 text-neutral-200 hover:bg-neutral-800"
              title="Toggle heatmap notice"
            >
              {showNotice ? "Hide Notice" : "Show Notice"}
            </button>
          </div>

          <div className="absolute bottom-2 right-2 flex gap-2 pointer-events-auto">
            <button
              onClick={() => navigate("/heatmap")}
              className="px-2 py-1 text-[11px] rounded bg-neutral-900/80 border border-neutral-700 text-neutral-200 hover:bg-neutral-800"
              title="Open full heatmap"
            >
              Expand
            </button>
          </div>

          {heatLoading && (
            <div className="absolute top-2 right-2 pointer-events-none">
              <div className="px-2 py-1 text-[11px] rounded bg-neutral-900/80 border border-neutral-700 text-neutral-300">
                Loading…
              </div>
            </div>
          )}
          {(heatError || lgsError) && (
            <div className="absolute top-2 right-2 translate-y-8 pointer-events-none max-w-[180px]">
              <div className="px-2 py-1 text-[11px] rounded bg-red-900/70 border border-red-600 text-red-200">
                {heatError || lgsError}
              </div>
            </div>
          )}
          {useHeatmapStore((s) => s._lgsSyncInFlight) && (
            <div className="absolute top-2 right-2 translate-y-16 pointer-events-none">
              <div className="px-2 py-1 text-[11px] rounded bg-neutral-900/70 border border-neutral-700 text-neutral-300">
                Syncing LGS…
              </div>
            </div>
          )}
          {points.length === 0 &&
            !heatLoading &&
            !heatError &&
            !lgsError && (
              <div className="absolute inset-0 flex items-center justify-center text-[12px] text-neutral-500 pointer-events-none">
                No player heat data
              </div>
            )}
        </div>
      </div>

      <div className="flex items-center justify-between">
        <HeatmapLegend showLgs compact={false} />
        <div className="text-[11px] text-neutral-500">
          {heatCells} pts · {lgsPoints.length} stores
        </div>
      </div>

      {showNotice && (
        <div className="mt-1 text-[11px] leading-snug text-neutral-300 bg-neutral-900/70 border border-neutral-700 rounded p-2">
          Player heatmap data is approximate & aggregated — it never reveals exact
          locations. Local Game Store markers are from public sources and may be
          outdated.
        </div>
      )}
    </div>
  );
}

export const HeatmapWidget = HeatmapWidgetImpl;
export default HeatmapWidgetImpl;

